import type { AuditLogEntry } from '@/api/audit';

interface MockAuditLogSeed {
  readonly id: string;
  readonly action: string;
  readonly entityType: string;
  readonly entityId?: string;
  readonly actor: string;
  readonly details?: Record<string, unknown>;
  readonly ipAddress?: string;
  readonly daysAgo: number;
  readonly hours: number;
  readonly minutes: number;
}

const buildTimestamp = (daysAgo: number, hours: number, minutes: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
};

const mockAuditLogSeeds: MockAuditLogSeed[] = [
  {
    id: 'audit-log-001',
    action: 'auth.login',
    entityType: 'auth',
    actor: 'alex.morgan',
    details: { method: 'password', browser: 'Chrome 136', status: 'success' },
    ipAddress: '198.51.100.24',
    daysAgo: 0,
    hours: 8,
    minutes: 12,
  },
  {
    id: 'audit-log-002',
    action: 'auth.mfa.verify',
    entityType: 'auth',
    actor: 'alex.morgan',
    details: { factor: 'totp', status: 'verified', device: 'Corporate laptop' },
    ipAddress: '198.51.100.24',
    daysAgo: 0,
    hours: 8,
    minutes: 13,
  },
  {
    id: 'audit-log-003',
    action: 'file.upload',
    entityType: 'file',
    entityId: '2f341e74-c0f1-4f93-9ba2-19fdce2f7301',
    actor: 'priya.shah',
    details: {
      fileName: 'quarterly-risk-review.pdf',
      sizeBytes: 4182932,
      classification: 'confidential',
      destination: 'Operations',
    },
    ipAddress: '203.0.113.16',
    daysAgo: 0,
    hours: 9,
    minutes: 6,
  },
  {
    id: 'audit-log-004',
    action: 'file.share',
    entityType: 'file',
    entityId: '2f341e74-c0f1-4f93-9ba2-19fdce2f7301',
    actor: 'priya.shah',
    details: {
      shareId: 'shr-91c2d8d0',
      recipient: 'vendor-auditor@partner.example',
      expiresInDays: 7,
    },
    ipAddress: '203.0.113.16',
    daysAgo: 0,
    hours: 9,
    minutes: 14,
  },
  {
    id: 'audit-log-005',
    action: 'file.download',
    entityType: 'file',
    entityId: '79c6df5f-28f7-46a3-9b69-56859f973641',
    actor: 'samuel.lee',
    details: {
      fileName: 'client-onboarding-checklist.xlsx',
      bytesTransferred: 932184,
      channel: 'browser',
    },
    ipAddress: '192.0.2.44',
    daysAgo: 1,
    hours: 14,
    minutes: 27,
  },
  {
    id: 'audit-log-006',
    action: 'user.create',
    entityType: 'user',
    entityId: '0f750240-b09c-4f76-a8cb-574cf8b2eac4',
    actor: 'jamie.chen',
    details: {
      username: 'maria.lopez',
      email: 'maria.lopez@hon.example',
      userType: 'internal',
    },
    ipAddress: '198.51.100.57',
    daysAgo: 1,
    hours: 10,
    minutes: 5,
  },
  {
    id: 'audit-log-007',
    action: 'user.role.assign',
    entityType: 'user',
    entityId: '0f750240-b09c-4f76-a8cb-574cf8b2eac4',
    actor: 'jamie.chen',
    details: {
      username: 'maria.lopez',
      role: 'Account Manager',
      scope: 'platform-ui',
    },
    ipAddress: '198.51.100.57',
    daysAgo: 1,
    hours: 10,
    minutes: 11,
  },
  {
    id: 'audit-log-008',
    action: 'group.create',
    entityType: 'group',
    entityId: 'c74cb4f2-67ab-4141-b847-1987300ac8aa',
    actor: 'noah.clark',
    details: {
      groupName: 'External Auditors',
      path: '/partners/external-auditors',
    },
    ipAddress: '203.0.113.73',
    daysAgo: 2,
    hours: 11,
    minutes: 35,
  },
  {
    id: 'audit-log-009',
    action: 'group.member.add',
    entityType: 'group',
    entityId: 'c74cb4f2-67ab-4141-b847-1987300ac8aa',
    actor: 'noah.clark',
    details: {
      groupName: 'External Auditors',
      username: 'vendor.auditor',
      role: 'viewer',
    },
    ipAddress: '203.0.113.73',
    daysAgo: 2,
    hours: 11,
    minutes: 49,
  },
  {
    id: 'audit-log-010',
    action: 'share.create',
    entityType: 'share',
    entityId: 'shr-91c2d8d0',
    actor: 'priya.shah',
    details: {
      fileId: '2f341e74-c0f1-4f93-9ba2-19fdce2f7301',
      recipient: 'vendor-auditor@partner.example',
      permissions: ['download'],
    },
    ipAddress: '203.0.113.16',
    daysAgo: 3,
    hours: 15,
    minutes: 2,
  },
  {
    id: 'audit-log-011',
    action: 'client.create',
    entityType: 'client',
    entityId: 'cli-88b729ba-1a7d-4d90-93ab-441c0487a7d8',
    actor: 'svc.platform-sync',
    details: {
      clientName: 'Northwind Parts',
      environment: 'prod',
      onboardingFlow: 'api-import',
    },
    ipAddress: '192.0.2.91',
    daysAgo: 3,
    hours: 16,
    minutes: 40,
  },
  {
    id: 'audit-log-012',
    action: 'client.update',
    entityType: 'client',
    entityId: 'cli-88b729ba-1a7d-4d90-93ab-441c0487a7d8',
    actor: 'svc.platform-sync',
    details: {
      clientName: 'Northwind Parts',
      changedFields: ['callbackUrl', 'mfaRequired'],
      mfaRequired: true,
    },
    ipAddress: '192.0.2.91',
    daysAgo: 4,
    hours: 9,
    minutes: 18,
  },
  {
    id: 'audit-log-013',
    action: 'auth.logout',
    entityType: 'auth',
    actor: 'alex.morgan',
    details: { sessionDurationMinutes: 74, reason: 'user_initiated' },
    ipAddress: '198.51.100.24',
    daysAgo: 4,
    hours: 17,
    minutes: 9,
  },
  {
    id: 'audit-log-014',
    action: 'file.delete',
    entityType: 'file',
    entityId: 'b0a38ca6-b352-4d1a-b765-1df5bf9f6f64',
    actor: 'taylor.nguyen',
    details: {
      fileName: 'expired-client-export.zip',
      deleteReason: 'retention_policy',
      retentionDays: 30,
    },
    ipAddress: '203.0.113.108',
    daysAgo: 5,
    hours: 13,
    minutes: 28,
  },
  {
    id: 'audit-log-015',
    action: 'file.download',
    entityType: 'file',
    entityId: '1d23b57e-b57e-49d3-9dcb-6aa0b4e3af53',
    actor: 'mia.garcia',
    details: {
      fileName: 'control-testing-evidence.docx',
      bytesTransferred: 202193,
      source: 'shared-link',
    },
    ipAddress: '198.51.100.84',
    daysAgo: 6,
    hours: 8,
    minutes: 46,
  },
  {
    id: 'audit-log-016',
    action: 'share.revoke',
    entityType: 'share',
    entityId: 'shr-91c2d8d0',
    actor: 'priya.shah',
    details: {
      revokedByPolicy: false,
      revocationReason: 'engagement_complete',
      recipient: 'vendor-auditor@partner.example',
    },
    ipAddress: '203.0.113.16',
    daysAgo: 6,
    hours: 16,
    minutes: 22,
  },
  {
    id: 'audit-log-017',
    action: 'user.update',
    entityType: 'user',
    entityId: '0f750240-b09c-4f76-a8cb-574cf8b2eac4',
    actor: 'jamie.chen',
    details: {
      username: 'maria.lopez',
      changedFields: ['lastName', 'department'],
      department: 'Risk Operations',
    },
    ipAddress: '198.51.100.57',
    daysAgo: 7,
    hours: 12,
    minutes: 14,
  },
  {
    id: 'audit-log-018',
    action: 'group.member.remove',
    entityType: 'group',
    entityId: 'e8b86d44-b9d5-4d1f-9c93-1ee4043140e3',
    actor: 'noah.clark',
    details: {
      groupName: 'Legacy Vendors',
      username: 'retired.vendor',
      reason: 'contract_ended',
    },
    ipAddress: '203.0.113.73',
    daysAgo: 8,
    hours: 10,
    minutes: 58,
  },
  {
    id: 'audit-log-019',
    action: 'file.upload',
    entityType: 'file',
    entityId: '4bfec2a1-9ff9-41ec-9456-f0417bb9d3aa',
    actor: 'olivia.king',
    details: {
      fileName: 'integration-test-results.csv',
      sizeBytes: 521908,
      classification: 'internal',
    },
    ipAddress: '192.0.2.132',
    daysAgo: 9,
    hours: 7,
    minutes: 39,
  },
  {
    id: 'audit-log-020',
    action: 'file.share',
    entityType: 'file',
    entityId: '4bfec2a1-9ff9-41ec-9456-f0417bb9d3aa',
    actor: 'olivia.king',
    details: {
      shareId: 'shr-a4f1d66c',
      recipient: 'qa-team@hon.example',
      expiresInDays: 3,
    },
    ipAddress: '192.0.2.132',
    daysAgo: 9,
    hours: 7,
    minutes: 45,
  },
  {
    id: 'audit-log-021',
    action: 'auth.login',
    entityType: 'auth',
    actor: 'ethan.reed',
    details: { method: 'sso', browser: 'Edge 135', status: 'success' },
    ipAddress: '198.51.100.141',
    daysAgo: 10,
    hours: 6,
    minutes: 55,
  },
  {
    id: 'audit-log-022',
    action: 'auth.mfa.verify',
    entityType: 'auth',
    actor: 'ethan.reed',
    details: { factor: 'webauthn', status: 'verified', authenticator: 'YubiKey 5' },
    ipAddress: '198.51.100.141',
    daysAgo: 10,
    hours: 6,
    minutes: 56,
  },
  {
    id: 'audit-log-023',
    action: 'file.download',
    entityType: 'file',
    entityId: '93e62416-5626-4766-8f08-8391f27aeeb5',
    actor: 'vendor.auditor',
    details: {
      fileName: 'evidence-package-2026-04.zip',
      bytesTransferred: 83190212,
      source: 'portal',
    },
    ipAddress: '203.0.113.155',
    daysAgo: 11,
    hours: 15,
    minutes: 24,
  },
  {
    id: 'audit-log-024',
    action: 'client.update',
    entityType: 'client',
    entityId: 'cli-2190d7fe-27c7-4d32-a2c3-b88789a8c50d',
    actor: 'amelia.ward',
    details: {
      clientName: 'Apex Logistics',
      changedFields: ['status'],
      status: 'suspended',
    },
    ipAddress: '192.0.2.188',
    daysAgo: 12,
    hours: 14,
    minutes: 3,
  },
  {
    id: 'audit-log-025',
    action: 'user.create',
    entityType: 'user',
    entityId: '0fb78a14-6690-428f-b29d-c2bafb615070',
    actor: 'jamie.chen',
    details: {
      username: 'liam.turner',
      email: 'liam.turner@partner.example',
      userType: 'client',
    },
    ipAddress: '198.51.100.57',
    daysAgo: 13,
    hours: 9,
    minutes: 17,
  },
  {
    id: 'audit-log-026',
    action: 'group.member.add',
    entityType: 'group',
    entityId: 'c74cb4f2-67ab-4141-b847-1987300ac8aa',
    actor: 'noah.clark',
    details: {
      groupName: 'External Auditors',
      username: 'liam.turner',
      role: 'contributor',
    },
    ipAddress: '203.0.113.73',
    daysAgo: 13,
    hours: 9,
    minutes: 42,
  },
  {
    id: 'audit-log-027',
    action: 'file.upload',
    entityType: 'file',
    entityId: '40f3b10f-dcb6-4b31-a202-e42f0f7846da',
    actor: 'samuel.lee',
    details: {
      fileName: 'annual-security-attestation.pdf',
      sizeBytes: 6119821,
      classification: 'restricted',
    },
    ipAddress: '192.0.2.44',
    daysAgo: 14,
    hours: 10,
    minutes: 6,
  },
  {
    id: 'audit-log-028',
    action: 'file.delete',
    entityType: 'file',
    entityId: '40f3b10f-dcb6-4b31-a202-e42f0f7846da',
    actor: 'samuel.lee',
    details: {
      fileName: 'annual-security-attestation.pdf',
      deleteReason: 'superseded_version',
    },
    ipAddress: '192.0.2.44',
    daysAgo: 15,
    hours: 8,
    minutes: 33,
  },
  {
    id: 'audit-log-029',
    action: 'share.create',
    entityType: 'share',
    entityId: 'shr-a4f1d66c',
    actor: 'olivia.king',
    details: {
      fileId: '4bfec2a1-9ff9-41ec-9456-f0417bb9d3aa',
      recipient: 'qa-team@hon.example',
      permissions: ['download', 'reshare'],
    },
    ipAddress: '192.0.2.132',
    daysAgo: 16,
    hours: 11,
    minutes: 57,
  },
  {
    id: 'audit-log-030',
    action: 'auth.logout',
    entityType: 'auth',
    actor: 'ethan.reed',
    details: { sessionDurationMinutes: 22, reason: 'idle_timeout' },
    ipAddress: '198.51.100.141',
    daysAgo: 18,
    hours: 12,
    minutes: 1,
  },
  {
    id: 'audit-log-031',
    action: 'user.update',
    entityType: 'user',
    entityId: '43a782d8-8b84-4a61-bf11-cdf85cc22d53',
    actor: 'jamie.chen',
    details: {
      username: 'harper.wells',
      changedFields: ['enabled'],
      enabled: false,
    },
    ipAddress: '198.51.100.57',
    daysAgo: 20,
    hours: 16,
    minutes: 12,
  },
  {
    id: 'audit-log-032',
    action: 'group.create',
    entityType: 'group',
    entityId: '2875b595-8fd2-4f13-93d3-a1f17ae56e5b',
    actor: 'noah.clark',
    details: {
      groupName: 'M&A Reviewers',
      path: '/deal-room/ma-reviewers',
    },
    ipAddress: '203.0.113.73',
    daysAgo: 22,
    hours: 13,
    minutes: 9,
  },
  {
    id: 'audit-log-033',
    action: 'client.create',
    entityType: 'client',
    entityId: 'cli-f8ddb634-9d9f-4592-b519-2ad7d4ae5131',
    actor: 'svc.platform-sync',
    details: {
      clientName: 'Contour Supply',
      environment: 'staging',
      onboardingFlow: 'manual',
    },
    ipAddress: '192.0.2.91',
    daysAgo: 24,
    hours: 9,
    minutes: 48,
  },
  {
    id: 'audit-log-034',
    action: 'file.download',
    entityType: 'file',
    entityId: '8d817c91-f82b-4f39-8102-cceab279011a',
    actor: 'mia.garcia',
    details: {
      fileName: 'regional-rollout-plan.pptx',
      bytesTransferred: 4189920,
      source: 'browser',
    },
    ipAddress: '198.51.100.84',
    daysAgo: 26,
    hours: 15,
    minutes: 43,
  },
  {
    id: 'audit-log-035',
    action: 'share.revoke',
    entityType: 'share',
    entityId: 'shr-7f2dd4f1',
    actor: 'taylor.nguyen',
    details: {
      revokedByPolicy: true,
      revocationReason: 'recipient_removed',
      recipient: 'temp.contractor@partner.example',
    },
    ipAddress: '203.0.113.108',
    daysAgo: 27,
    hours: 12,
    minutes: 34,
  },
  {
    id: 'audit-log-036',
    action: 'auth.login',
    entityType: 'auth',
    actor: 'mia.garcia',
    details: { method: 'sso', browser: 'Firefox 138', status: 'success' },
    ipAddress: '198.51.100.84',
    daysAgo: 29,
    hours: 7,
    minutes: 22,
  },
];

export const mockAuditLogs: AuditLogEntry[] = mockAuditLogSeeds
  .map((seed) => ({
    id: seed.id,
    action: seed.action,
    entityType: seed.entityType,
    entityId: seed.entityId,
    actor: seed.actor,
    details: seed.details,
    ipAddress: seed.ipAddress,
    createdAt: buildTimestamp(seed.daysAgo, seed.hours, seed.minutes),
  }))
  .sort(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );
